import { useTranslation } from 'next-i18next';
import {
  CheckIcon, PencilIcon, NoSymbolIcon, ArchiveBoxIcon,
} from '@heroicons/react/24/solid';
import { PersonCheckIcon } from '@components/common/Icons';
import {
  Anchor,
  Button,
  LinkStyled,
  TailwindSpinner,
  Tooltip,
} from '@components/common';
import { ApiCompanyJobDetail } from '@type/v1-api-types';
import { cn } from '@utils/cn';
import { SuspendConfirmationDialog } from '@components/templates/my-jobs/SuspendConfirmationDialog';
import { useCompanyJobActions } from '@components/templates/my-jobs/hooks/useCompanyJobActions';

export interface CompanyJobActionsProps {
  job: ApiCompanyJobDetail;
  isSinglePage?: boolean;
  revalidatePage?: () => void;
}

/**
 * @description Job detail - Job Actions component.
 * - **job** - ApiCompanyJobDetail. The job detail.
 * - **isSinglePage** - boolean. If true, the component is rendered as a single page.
 * - **revalidatePage** - A function to revalidate the page after activating/deactivating a job.
 */
export const CompanyJobActions = (props: CompanyJobActionsProps) => {
  // Destructure props
  const { job, isSinglePage, revalidatePage } = props;

  // Translation
  const { t } = useTranslation('common');

  // Company Job Actions Hook
  const {
    handleJobActivation,
    handleJobSuspension,
    handleSuspensionConfirmation,
    handleSuspensionCancellation,
    handleTopJobSuspension,
    isActivating,
    isSuspending,
    isSuspendingTopJob,
    isSuspensionConfirmationOpen,
  } = useCompanyJobActions({
    jobSlug: job.slug,
    workplaceJobSlug: job.ownerDetail?.workplaceJobSlug,
    workplaceSlug: job.workplace?.slug,
    eligibleForInstantActivation: job.ownerDetail?.eligibleForInstantActivation,
    onSuspension: revalidatePage,
  });

  // Helper Classes
  // *****************************************
  const buttonBaseClasses = 'px-5 !transition !duration-200 lg:!px-7';
  const buttonDefaultClasses = '!bg-primary/10 !text-primary';
  const editButtonClasses = cn(
    buttonBaseClasses,
    buttonDefaultClasses,
    'hover:!bg-primary hover:!text-white',
  );
  const suspendButtonClasses = cn(
    buttonBaseClasses,
    '!bg-error/10 !text-error hover:!bg-error hover:!text-white',
  );

  return (
    <>
      <div
        className={`${
          isSinglePage ? 'bottom-16 md:bottom-2' : 'bottom-2'
        } sticky z-2 -ml-2 mt-4 flex space-x-2 rounded-full bg-surface px-2 py-2.5 shadow-jobActionsContainer lg:bottom-4 lg:-mx-4 lg:mt-8`}
      >
        {/* Suspend Job Button - For jobs added by company */}
        {job.ownerDetail?.suspend && !job.ownerDetail?.workplaceJobSlug && (
          <Tooltip
            tooltipText={t('my-jobs.card.actions.suspend')}
            position="top"
            colorScheme="black"
            floating
            useFloatingPortal
            className="relative inline-flex"
            triggerContainerClass="inline-flex"
          >
            <Button
              styling="solid"
              color="error"
              rounding="full"
              className={suspendButtonClasses}
              aria-label={t('my-jobs.card.actions.suspend')}
              onClick={handleJobSuspension}
            >
              <NoSymbolIcon className="size-5" />
            </Button>
          </Tooltip>
        )}
        {/* Suspend Job Button - For top jobs */}
        {job.ownerDetail?.suspend && job.ownerDetail?.workplaceJobSlug && (
          <Tooltip
            tooltipText={t('my-jobs.card.actions.archive')}
            position="top"
            colorScheme="black"
            floating
            useFloatingPortal
            className="relative inline-flex"
            triggerContainerClass="inline-flex"
          >
            <Button
              styling="solid"
              color="error"
              rounding="full"
              className={suspendButtonClasses}
              aria-label={t('my-jobs.card.actions.archive')}
              onClick={() => { void handleTopJobSuspension(); }}
            >
              {
                isSuspendingTopJob
                  ? <TailwindSpinner className="size-5" />
                  : <ArchiveBoxIcon className="size-5" />
              }
            </Button>
          </Tooltip>
        )}
        <Tooltip
          tooltipText={t('my-jobs.card.actions.edit')}
          position="top"
          colorScheme="black"
          floating
          useFloatingPortal
          className="relative inline-flex"
          triggerContainerClass="inline-flex"
        >
          <LinkStyled
            href={`/job-add/edit/${job.slug}`}
            styling="solid"
            color="primary"
            rounding="full"
            className={editButtonClasses}
          >
            <PencilIcon className="size-5" />
          </LinkStyled>
        </Tooltip>
        <Anchor
          href="company_upload_job_cv"
          symfonyRoute
          symfonyParams={{ slug: job.slug }}
          color="secondary"
          rounding="full"
          className="grow gap-2 !transition !duration-200"
        >
          <PersonCheckIcon className="size-5" />
          <span>{t('company.label.upload.cv')}</span>
        </Anchor>
        {job.ownerDetail?.activate && (
          <Button
            className="grow gap-2 !transition !duration-200"
            color="secondary"
            rounding="full"
            isLoading={isActivating}
            onClick={() => {
              void handleJobActivation();
            }}
          >
            <CheckIcon className="size-6" />
            <span>{t('global.action.activate')}</span>
          </Button>
        )}
      </div>
      <SuspendConfirmationDialog
        open={isSuspensionConfirmationOpen}
        onConfirm={() => {
          void handleSuspensionConfirmation();
        }}
        onCancel={handleSuspensionCancellation}
        isConfirmLoading={isSuspending}
      />
    </>
  );
};
